export const UserType = {
  INTERNAL: "internal",
  ORGANIZATION_MEMBER: "organization_member",
} as const;

export type UserType = (typeof UserType)[keyof typeof UserType];

export const UserRole = {
  ADMIN: "admin",
  OPERATOR: "operator",
  SUPPORT: "support",
  DEVELOPER: "developer",
  USER: "user",
  VIEWER: "viewer",
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const UserStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  SUSPENDED: "suspended",
  PENDING_VERIFICATION: "pending_verification",
} as const;

export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

/**
 * Body of `POST /internal/users`.
 *
 * The shape encodes three backend rules so they cannot be got wrong at a call site:
 *
 * 1. **`organization_id` is required.** An INTERNAL caller that omits it gets `organization_member` by default
 *    and then a NOT NULL violation — a **500**, not a validation error.
 * 2. **`user_type` is absent on purpose.** The only value this screen can create is the backend default,
 *    `organization_member`; asking for `internal` is refused when paired with an organization and violates
 *    NOT NULL without one, so INTERNAL users cannot be created through this endpoint at all.
 * 3. **`status` and `two_factor_enabled` are required, not optional.** Their entity defaults
 *    (`pending_verification` and `true`) produce an account that cannot enrol in 2FA and cannot sign in, so
 *    the caller is forced to state them.
 *
 * `role` and `scopes` are deliberately not here — but NOT because "the guards ignore them". `scopes` at
 * creation is load-bearing: the service homologates it into real RBAC assignments, falling back to
 * `MEMBER_DEFAULT_SCOPES` (read on every non-sensitive module) when it is empty. Omitting it is what produces
 * exactly the read-only starting state the drawer advertises. `role` really is inert.
 */
export interface CreateUserPayload {
  full_name: string;
  email: string;
  password: string;
  organization_id: string;
  status: UserStatus;
  two_factor_enabled: boolean;
  country?: string;
  phone_number?: string;
  preferred_language?: string;
}

/**
 * Body of `PATCH /internal/users/:id` — **a diff, never the whole object.**
 *
 * `user_type` and `organization_id` are structurally absent, which is what makes the live 400 impossible:
 * echoing an INTERNAL user's `user_type: "internal"` back together with their (root) `organization_id` is
 * rejected by the service, and that is why editing Coloca staff fails on the screen this replaces.
 *
 * Nullable fields accept `null` to CLEAR them. They must never receive `""` — `@IsOptional()` skips `null`
 * but not the empty string, so `""` reaches the validator and 400s.
 */
export interface UpdateUserPayload {
  full_name?: string;
  email?: string;
  password?: string;
  status?: UserStatus;
  country?: string | null;
  phone_number?: string | null;
  preferred_language?: string | null;
}
